import {
  EQUIVALENT,
  FORMATIONS,
  type FormationCode,
  SLOT_FALLBACK,
} from '../data/positions.ts';
import type { DepthOrder, Player, PositionCode, SlotSpec } from '../types.ts';
import { DEPTH_POSITIONS } from './depth.ts';
import { matchesPreferredSide, suitability } from './rules.ts';

export interface SlotBoard {
  /** One ordered queue per formation slot: index 0 is the starter, the rest are cover. */
  queues: Player[][];
  /** Players no slot could take, even through their fallback positions. */
  overflow: Player[];
}

/** The dataset's own position list per player, which the fallback reads. */
export function datasetPositionMap(rawPlayers: Player[]): Map<string, PositionCode[]> {
  return new Map(rawPlayers.map((p) => [p.id, p.positions]));
}

/** How a slot may be fed, best first. */
type SlotFit = 'exact' | 'equivalent' | 'fallback';

const FIT_RANK: Record<SlotFit, number> = { exact: 0, equivalent: 1, fallback: 2 };

/**
 * A slot can be fed by its own position, by one the rules treat as the same role, or —
 * last — by a row `SLOT_FALLBACK` allows when the squad has nobody filed under its own
 * position at all.
 */
function slotAccepts(slot: SlotSpec, code: PositionCode): SlotFit | null {
  if (slot[0] === code) return 'exact';
  if ((EQUIVALENT[slot[0]] ?? []).indexOf(code) >= 0) return 'equivalent';
  if ((SLOT_FALLBACK[slot[0]] ?? []).indexOf(code) >= 0) return 'fallback';
  return null;
}

/**
 * Of the slots that can take this player, the one he should join.
 *
 * Emptiest first, so a pair of mirrored slots fills alternately rather than stacking;
 * then an exact position beats an equivalent one; then — only while the chart is still
 * the dataset's — his flank preference; and finally the formation's own left-to-right
 * order, which is what decides once the user has taken over.
 */
function pickSlot(
  candidates: { index: number; kind: SlotFit }[],
  queues: Player[][],
  slots: readonly SlotSpec[],
  player: Player,
  untouchedChart: boolean,
): number {
  return candidates
    .slice()
    .sort(
      (a, b) =>
        queues[a.index].length - queues[b.index].length ||
        FIT_RANK[a.kind] - FIT_RANK[b.kind] ||
        (untouchedChart
          ? Number(matchesPreferredSide(player, slots[b.index])) -
            Number(matchesPreferredSide(player, slots[a.index]))
          : 0) ||
        a.index - b.index,
    )[0].index;
}

/**
 * Lays the squad out over a formation's slots — the single arrangement the pitch, the
 * depth chart and the poster all read, so the three can never disagree.
 *
 * Players are taken in the order the depth chart lists them, and each joins a slot of
 * the position he is filed under. When his position has no slot in this formation at all
 * — Ön Libero in a 4-2-3-1, say — he falls back to his **dataset** second, third or
 * fourth position, in that order, rather than being dropped. Only a player no slot can
 * take at all ends up in `overflow`.
 */
export function buildBoard(
  formation: FormationCode,
  firstTeam: Player[],
  depth: DepthOrder,
  datasetPositions: Map<string, PositionCode[]>,
  /**
   * Whether the depth chart is still the dataset's own. While it is, the board is free to
   * rank on merit and to honour `preferredSide`. Once the user has rearranged the chart
   * his order *is* the intent, so both fall silent and the chart alone decides.
   */
  untouchedChart: boolean,
): SlotBoard {
  const slots = FORMATIONS[formation];
  const queues: Player[][] = slots.map(() => []);
  const byId = new Map(firstTeam.map((p) => [p.id, p]));
  const placed = new Set<string>();

  const candidatesFor = (code: PositionCode) =>
    slots
      .map((slot, index) => ({ index, kind: slotAccepts(slot, code) }))
      .filter((c): c is { index: number; kind: SlotFit } => c.kind !== null);

  // Pass one — everyone whose position exists in this formation, in depth-chart order.
  const stranded: Player[] = [];
  for (const code of DEPTH_POSITIONS) {
    const candidates = candidatesFor(code);
    for (const id of depth[code] ?? []) {
      const player = byId.get(id);
      if (!player || placed.has(id)) continue;
      if (!candidates.length) {
        stranded.push(player);
        continue;
      }
      queues[pickSlot(candidates, queues, slots, player, untouchedChart)].push(player);
      placed.add(id);
    }
  }

  // Pass two — the stranded fall back through their dataset positions, second choice
  // first. Their depth position is skipped: it is the one this formation does not have.
  const overflow: Player[] = [];
  for (const player of stranded) {
    const fallbacks = (datasetPositions.get(player.id) ?? player.positions).filter(
      (code) => code !== player.positions[0],
    );
    let seated = false;
    for (const code of fallbacks) {
      const candidates = candidatesFor(code);
      if (!candidates.length) continue;
      queues[pickSlot(candidates, queues, slots, player, untouchedChart)].push(player);
      placed.add(player.id);
      seated = true;
      break;
    }
    if (!seated) overflow.push(player);
  }

  // Rank each queue on merit rather than on which depth row its players arrived from.
  //
  // Two players can both be a 5 at Merkez OS and still live in different rows — one
  // wrote `DM` first, the other `CM` — and a formation that has no Ön Libero slot seats
  // the DM row only in pass two, behind everyone already placed. Without this a 6,64
  // covers ahead of a 7,05 purely because of the row he happens to sit in.
  //
  // Only the order inside a queue changes; which slot a player was given does not, so
  // the mirrored pair stays balanced and `preferredSide` still decides his flank.
  if (untouchedChart) {
    queues.forEach((queue, index) => {
      const code = slots[index][0];
      queue.sort((a, b) => suitability(b, code) - suitability(a, code) || b.rating - a.rating);
    });
  }

  return { queues, overflow };
}

/** The starting eleven implied by the board: whoever heads each slot's queue. */
export function lineupFromBoard(board: SlotBoard): Record<number, string> {
  const lineup: Record<number, string> = {};
  board.queues.forEach((queue, index) => {
    if (queue[0]) lineup[index] = queue[0].id;
  });
  return lineup;
}
